import {isMediaCardType} from '@wandb/weave/common/types/media';
import {ID} from '@wandb/weave/common/util/id';
import {TargetBlank} from '@wandb/weave/common/util/links';
// @todo: Migrate to @wandb/components. For more details see:
// https://www.notion.so/wandbai/Migrating-components-207e2f5c7ef380129201f72a5770a8db
// eslint-disable-next-line wandb/no-deprecated-imports
import {Button} from '@wandb/weave/components/Button';
import {Callout} from '@wandb/weave/components/Callout';
import {Pill} from '@wandb/weave/components/Tag';
import * as React from 'react';
import {useCallback} from 'react';
import {twMerge} from 'tailwind-merge';

import {
  AddPanelsAction,
  trackAddPanels,
} from '../../../services/analytics/addPanelsEvents';
import DOC_URLS, {lookupDocUrl} from '../../../util/doc_urls';
import {LayedOutPanel} from '../../../util/panelTypes';
import {panelViews} from '../../PanelBank/types';
import {usePanelsQuickAddContext} from '../PanelsQuickAdd/PanelsQuickAddContext';
import {useWorkspaceDrawerContext} from '../WorkspaceDrawerContextProvider';
import {
  DEFAULT_CONFIGS_BY_PANEL_TYPE,
  PANEL_VIEWS_BY_PANEL_TYPE,
} from './constants';
import {useIconColorContext} from './contexts/IconColorContext';
import {useNewPanelEditorContext} from './contexts/NewPanelEditorContext';
import type {AddPanelsMenuItemProps} from './types';

export const AddPanelsMenuItem = ({
  type,
  iconName,
  label,
  description,
  isDisabled = false,
  isRecommended = false,
}: AddPanelsMenuItemProps) => {
  const {openWorkspaceDrawer, targetSectionRef} = useWorkspaceDrawerContext();
  const {openQuickAdd} = usePanelsQuickAddContext();
  const {setNewPanel} = useNewPanelEditorContext();
  const iconColor = useIconColorContext();

  const trackAddPanelsBound = useCallback(
    (action: AddPanelsAction) => {
      trackAddPanels({
        action,
        scope: !targetSectionRef ? 'global' : 'section',
        panelType: type,
      });
    },
    [targetSectionRef, type]
  );

  const onSelect = useCallback(() => {
    if (isDisabled) {
      return;
    }

    trackAddPanelsBound('Panel type selected');

    if (type === 'quick-add') {
      openQuickAdd({
        location: 'Add panels drawer',
        sectionRef: targetSectionRef,
        onBack: () => openWorkspaceDrawer('add-panels', targetSectionRef),
      });
      return;
    }

    const viewType = isMediaCardType(type)
      ? panelViews.mediaBrowser
      : PANEL_VIEWS_BY_PANEL_TYPE[type];
    if (viewType) {
      const isWeaveTrace = viewType === panelViews.weaveTrace;
      setNewPanel({
        __id__: ID(),
        // Provide a stable key so this manual panel matches the
        // auto-generated API-added spec with key 'weave-trace'.
        ...(isWeaveTrace ? {key: 'weave-trace'} : {}),
        viewType,
        config: DEFAULT_CONFIGS_BY_PANEL_TYPE[type] ?? {},
        isAuto: false,
      } as LayedOutPanel);
    }
  }, [
    isDisabled,
    openQuickAdd,
    openWorkspaceDrawer,
    setNewPanel,
    targetSectionRef,
    trackAddPanelsBound,
    type,
  ]);

  return (
    <div
      role="menuitem"
      data-test={`add-panel-${type}`}
      aria-disabled={isDisabled}
      className={twMerge(
        'flex items-center justify-between gap-12 rounded-lg px-16 py-8',
        !isDisabled && 'cursor-pointer hover:bg-moon-100',
        isDisabled && '[&:hover_a]:opacity-100'
      )}
      onClick={onSelect}>
      <div
        className={twMerge(
          'flex items-center gap-12',
          isDisabled && 'opacity-40'
        )}>
        <Callout color={iconColor} icon={iconName} size="x-small" />
        <div>
          <p className="font-semibold">{label}</p>
          <p className="text-sm text-moon-500">{description}</p>
        </div>
      </div>
      {isDisabled ? (
        <TargetBlank
          href={lookupDocUrl(type) ?? DOC_URLS.logs}
          className="opacity-0 transition-opacity duration-100">
          <Button
            size="small"
            variant="secondary"
            endIcon="open-new-tab"
            onClick={() => trackAddPanelsBound('Disabled item docs clicked')}>
            Docs
          </Button>
        </TargetBlank>
      ) : isRecommended ? (
        <Pill color="cactus" label="Recommended" />
      ) : null}
    </div>
  );
};
